"use client";

import { Clock, Loader2, MapPin, Navigation } from "lucide-react";
import * as React from "react";

import { NEARBY_VALUE } from "@/components/shared/location-dropdown/use-location-dropdown";
import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { jobFiltersTestIds } from "@/lib/data-test-ids/jobs.data-test-ids";
import { cn } from "@/lib/utils";

interface LocationCommandGroupProps {
  heading: string;
  children: React.ReactNode;
}

const LocationCommandGroup = ({
  heading,
  children,
}: LocationCommandGroupProps) => {
  return (
    <CommandGroup
      heading={
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {heading}
        </span>
      }
    >
      {children}
    </CommandGroup>
  );
};

interface LocationCommandItemProps {
  icon?: React.ReactNode;
  value: string;
  label: string;
  dataTestId?: string;
  labelClassName?: string;
  onSelect: () => void;
}

const LocationCommandItem = ({
  icon,
  value,
  label,
  labelClassName,
  onSelect,
  dataTestId,
}: LocationCommandItemProps) => {
  return (
    <CommandItem
      key={value}
      value={value}
      onSelect={onSelect}
      className="py-2 px-3 cursor-pointer"
      data-testid={dataTestId}
    >
      {icon}
      <span className={cn("font-medium truncate", labelClassName)}>
        {label}
      </span>
    </CommandItem>
  );
};

interface Prediction {
  value: string;
  label: string;
}

interface GetSuggestionsListParams {
  topCities: readonly string[];
  topCitiesLabel?: string;
  value?: string;
  searchTerm: string;
  handleSelect: (val: string) => void;
  recentSearches: readonly string[];
  isResolvingLocation: boolean;
  predictions: readonly Prediction[];
}

const enum DropdownView {
  Idle = "idle",
  Searching = "searching",
}

export const getSuggestionsList = ({
  topCities,
  topCitiesLabel,
  value,
  searchTerm,
  handleSelect,
  recentSearches,
  isResolvingLocation,
  predictions,
}: GetSuggestionsListParams) => {
  const view: DropdownView =
    value !== searchTerm ? DropdownView.Searching : DropdownView.Idle;

  return (
    <>
      <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
        No locations found
      </CommandEmpty>

      {view === DropdownView.Idle && (
        <>
          {recentSearches.length > 0 && (
            <LocationCommandGroup heading="Recent Searches">
              {recentSearches.map((term: string) => (
                <LocationCommandItem
                  key={`recent-${term}`}
                  value={`recent-${term}`}
                  icon={
                    <Clock className="mr-3 h-4 w-4 text-muted-foreground shrink-0" />
                  }
                  label={term}
                  onSelect={() => handleSelect(term)}
                  dataTestId={jobFiltersTestIds.LOCATION_FILTER_RECENT}
                />
              ))}
            </LocationCommandGroup>
          )}

          <LocationCommandGroup heading="Near You">
            <LocationCommandItem
              value={NEARBY_VALUE}
              icon={
                isResolvingLocation ? (
                  <Loader2 className="mr-3 h-4 w-4 animate-spin text-primary shrink-0" />
                ) : (
                  <Navigation className="mr-3 h-4 w-4 text-primary fill-primary/20 shrink-0" />
                )
              }
              label="Use my current location"
              labelClassName="text-primary"
              onSelect={() => handleSelect(NEARBY_VALUE)}
              dataTestId={jobFiltersTestIds.LOCATION_FILTER_NEARBY}
            />
          </LocationCommandGroup>

          {topCities.length > 0 && (
            <LocationCommandGroup
              heading={topCitiesLabel || "Popular Locations"}
            >
              {topCities.map((city) => (
                <LocationCommandItem
                  key={`city-${city}`}
                  value={`city-${city}`}
                  icon={
                    <MapPin className="mr-3 h-4 w-4 text-muted-foreground shrink-0" />
                  }
                  label={city}
                  onSelect={() => handleSelect(city)}
                  dataTestId={jobFiltersTestIds.LOCATION_FILTER_TOP_CITY}
                />
              ))}
            </LocationCommandGroup>
          )}
        </>
      )}

      {view === DropdownView.Searching && predictions.length > 0 && (
        <CommandGroup>
          {predictions.map((pred) => (
            <LocationCommandItem
              key={pred.value}
              value={pred.value}
              icon={
                <MapPin className="mr-3 h-4 w-4 text-muted-foreground shrink-0" />
              }
              label={pred.label}
              onSelect={() => handleSelect(pred.value)}
              dataTestId={jobFiltersTestIds.LOCATION_FILTER_PREDICTION}
            />
          ))}
        </CommandGroup>
      )}
    </>
  );
};
